import { useEffect, useState } from 'react';

import {
    ActionIcon,
    Alert,
    Button,
    Group,
    LoadingOverlay,
    NumberInput,
    Paper,
    Stack,
    Text,
    Title
} from '@mantine/core';

import {
    IconPlus,
    IconTrash
} from '@tabler/icons-react';

import { getExchangeRate } from './currency.service';

const createPrice = () => ({
    id: crypto.randomUUID(),
    amount: null
});

const Calculator = () => {
    const [prices, setPrices] = useState([createPrice()]);
    const [exchangeRate, setExchangeRate] = useState(null);
    const [conversionDate, setConversionDate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadExchangeRate = async () => {
            try {
                setLoading(true);
                setError(null);

                const data = await getExchangeRate();

                setExchangeRate(Number(data.ConversionRate));
                setConversionDate(data.ConversionDate);
            } catch (err) {
                console.error(
                    'Error loading exchange rate:',
                    err
                );

                setError(
                    'No fue posible obtener el tipo de cambio.'
                );
            } finally {
                setLoading(false);
            }
        };

        loadExchangeRate();
    }, []);

    const handleAddPrice = () => {
        setPrices((currentPrices) => [
            ...currentPrices,
            createPrice()
        ]);
    };

    const handleRemovePrice = (id) => {
        setPrices((currentPrices) => {
            if (currentPrices.length === 1) {
                return currentPrices;
            }

            return currentPrices.filter(
                (price) => price.id !== id
            );
        });
    };

    const handleAmountChange = (id, value) => {
        setPrices((currentPrices) =>
            currentPrices.map((price) =>
                price.id === id
                    ? {
                        ...price,
                        amount: value
                    }
                    : price
            )
        );
    };

    const calculateUsd = (amount) => {
        if (
            !amount ||
            amount <= 0 ||
            !exchangeRate ||
            exchangeRate <= 0
        ) {
            return null;
        }

        return amount / exchangeRate;
    };

    const formatCurrency = (value, currency) => {
        if (
            value === null ||
            value === undefined
        ) {
            return '-';
        }

        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatDate = (date) => {
        if (!date) {
            return '-';
        }

        const [year, month, day] = date.split('-');

        return `${day}/${month}/${year}`;
    };

    return (
        <Stack p="md" gap="lg">
            <div>
                <Title order={2}>
                    Calculadora
                </Title>

                <Text c="dimmed" mt="xs">
                    Ingresa uno o más precios en pesos mexicanos para
                    obtener su equivalente en dólares.
                </Text>
            </div>

            {error && (
                <Alert
                    color="red"
                    title="Error"
                >
                    {error}
                </Alert>
            )}

            <Paper
                withBorder
                p="lg"
                radius="md"
                pos="relative"
            >
                <LoadingOverlay
                    visible={loading}
                    overlayProps={{
                        radius: 'md',
                        blur: 1
                    }}
                />

                <Stack gap={4}>
                    <Group
                        justify="space-between"
                        align="center"
                    >
                        <Text fw={600}>
                            Tipo de cambio
                        </Text>

                        <Text fw={700}>
                            {exchangeRate
                                ? `1 USD = ${formatCurrency(
                                    exchangeRate,
                                    'MXN'
                                )}`
                                : '-'}
                        </Text>
                    </Group>

                    <Text size="sm" c="dimmed">
                        Fecha de consulta:{' '}
                        {formatDate(conversionDate)}
                    </Text>
                </Stack>
            </Paper>

            <Paper withBorder p="lg" radius="md">
                <Stack gap="md">
                    {prices.map((price, index) => {
                        const usdValue = calculateUsd(price.amount);

                        return (
                            <Group
                                key={price.id}
                                align="flex-end"
                                wrap="nowrap"
                            >
                                <Text
                                    fw={600}
                                    w={30}
                                    pb={10}
                                >
                                    {index + 1}
                                </Text>

                                <NumberInput
                                    label="Precio en MXN"
                                    placeholder="Ingresa el importe"
                                    prefix="$ "
                                    thousandSeparator=","
                                    decimalScale={2}
                                    fixedDecimalScale
                                    min={0}
                                    hideControls
                                    value={price.amount}
                                    onChange={(value) =>
                                        handleAmountChange(
                                            price.id,
                                            value
                                        )
                                    }
                                    style={{ flex: 1 }}
                                    disabled={
                                        loading ||
                                        !exchangeRate
                                    }
                                />

                                <Stack
                                    gap={2}
                                    style={{ flex: 1 }}
                                >
                                    <Text size="sm" fw={500}>
                                        Precio en USD
                                    </Text>

                                    <Text
                                        size="lg"
                                        fw={700}
                                    >
                                        {formatCurrency(
                                            usdValue,
                                            'USD'
                                        )}
                                    </Text>
                                </Stack>

                                {prices.length > 1 && (
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        size="lg"
                                        onClick={() =>
                                            handleRemovePrice(price.id)
                                        }
                                        aria-label="Eliminar precio"
                                    >
                                        <IconTrash size={18} />
                                    </ActionIcon>
                                )}
                            </Group>
                        );
                    })}

                    <Button
                        variant="light"
                        leftSection={<IconPlus size={18} />}
                        onClick={handleAddPrice}
                        disabled={
                            loading ||
                            !exchangeRate
                        }
                    >
                        Agregar precio
                    </Button>
                </Stack>
            </Paper>
        </Stack>
    );
};

export default Calculator;